import React, { useState, useRef, useEffect, useMemo } from "react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useAuthStore from "@/stores/user/authStore";
import useToraStore from "@/stores/tripBuilder/useToraStore";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import { AIService, TripContext } from "@/services/chatService";
import { useChat } from "@/hooks/useChat";
import MessageSkeleton from "./MessageSkeleton";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import { EnhancedChatMessage } from "@/types/chat";
import { useQueryClient } from "@tanstack/react-query";
import { StreamHandler } from "@/services/streamHandlerService";
import { MapPin, Compass, Info, RefreshCw, Plane } from "lucide-react";
import logger from "@/utils/logger";

const CONVERSATION_STARTERS = (locationName: string) => [
  {
    icon: <MapPin className="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />,
    text: "Plan my itinerary",
    prompt: `Can you help me plan an itinerary for my trip to ${locationName}?`,
  },
  {
    icon: <Compass className="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />,
    text: "Suggest activities",
    prompt: `What are the must-do activities in ${locationName}?`,
  },
  {
    icon: <Plane className="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />,
    text: `Transport to ${locationName}`,
    prompt: `Find some transport options for getting to ${locationName}`,
  },
  {
    icon: <Info className="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />,
    text: "Local tips",
    prompt: `What are some local tips and things I should know about ${locationName}?`,
  },
];

const ToraContent: React.FC = () => {
  const { selectedLocation, tripData, getChatItineraryItems, updateLocation } =
    useTripStore();
  const user = useAuthStore((state) => state.user);
  const { data: chatHistory, isLoading: chatHistoryLoading } = useChat(
    tripData?.id || "",
    selectedLocation!
  );
  const [input, setInput] = useState<string>("");
  const [chatId, setChatId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [processingMessage, setProcessingMessage] = useState<string>("");
  const [messageSending, setMessageSending] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [awaitMessage, setAwaitMessage] = useState<string>("");
  const [flightSearchMessage, setFlightSearchMessage] = useState<string>("");
  const messagesEndRef = useRef(null);
  const queryClient = useQueryClient();
  const {
    setShowRecommendations,
    setShowAccommodations,
    setChatMessages,
    getChatMessages,
    clearAll,
    setIsLoading: setSuggestionsLoading,
  } = useToraStore();
  const transportData = useTransportStore((state) => state.transportData);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const streamingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const chatMessages = getChatMessages(selectedLocation?.id || "");

  useEffect(() => {
    streamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    if (chatHistory && selectedLocation) {
      setChatId(chatHistory.chat_id);
      if (chatHistory.messages.length > 0) {
        setChatMessages(selectedLocation.id, chatHistory.messages);
      }
    }
  }, [
    chatHistory,
    selectedLocation,
    setChatId,
    setChatMessages,
    getChatMessages,
  ]);

  // Clear recommendations when location changes
  useEffect(() => {
    if (selectedLocation) {
      setShowRecommendations(false);
      setShowAccommodations(false);
      clearAll();
    }
  }, [
    selectedLocation,
    setShowRecommendations,
    setShowAccommodations,
    clearAll,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      (messagesEndRef.current as HTMLElement).scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [chatMessages]);

  const resetStreamTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (isStreaming) {
        // Abort the stream if it takes too long
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Set error message
        setChatMessages(selectedLocation?.id || "", (prevMessages) => {
          const updatedMessages = [...prevMessages];
          const lastMessage = updatedMessages[updatedMessages.length - 1];
          if (lastMessage && !lastMessage.isUser) {
            lastMessage.status = "error";
            lastMessage.text =
              "Sorry, couldn't process your request. Please try again.";
          }
          return updatedMessages;
        });

        // Reset state
        setIsLoading(false);
        setIsStreaming(false);
        setFlightSearchMessage("");
        setAwaitMessage("");
      }
    }, 10000); // 10 seconds timeout
  };

  const abortStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const tripContext: TripContext = useMemo(
    () => ({
      trip: {
        trip_id: tripData?.id || "",
        itinerary: getChatItineraryItems().map((item) => ({
          date: item.date,
          items: item.items,
        })),
      },
      location: {
        id: selectedLocation?.id || "",
        name: selectedLocation?.name || "",
        timezone: selectedLocation?.date?.time_zone || "",
        country: selectedLocation?.address_components?.country || "",
        coordinates: {
          latitude: Number(selectedLocation?.coordinates?.latitude) || 0,
          longitude: Number(selectedLocation?.coordinates?.longitude) || 0,
        },
      },
      transport: {
        from_location: transportData.locationAbove?.name || "",
        current_location: selectedLocation?.name || "",
        to_location: transportData.locationBelow?.name || "",
      },
      date: {
        start_date: selectedLocation?.date?.start_date || "",
        end_date: selectedLocation?.date?.end_date || "",
      },
      waytora_id: selectedLocation?.waytora_id || "",
      viator_id: selectedLocation?.viator_id.toString() || "",
    }),
    [tripData, selectedLocation, transportData, getChatItineraryItems]
  );

  const generateMessageId = () =>
    `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // handle send function
  const handleSend = async (message: string = input) => {
    if (!message.trim() || isStreaming) return;

    // Create a new AbortController for this stream
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setMessageSending(true);
    setIsStreaming(true);
    let streamId = "";
    let streamStarted = false;

    const userMessage: EnhancedChatMessage = {
      id: generateMessageId(),
      text: message,
      isUser: true,
      timestamp: new Date().toISOString(),
      status: "sent",
    };

    const aiMessage: EnhancedChatMessage = {
      id: generateMessageId(),
      text: "",
      isUser: false,
      timestamp: new Date().toISOString(),
      status: "sending",
    };

    try {
      setTimeout(() => {
        setChatMessages(selectedLocation?.id || "", (prevMessages) => [
          ...prevMessages,
          userMessage,
          aiMessage,
        ]);
        scrollToBottom();
        setInput("");
        setIsLoading(true);
        setMessageSending(false);
      }, 750);

      // Start timeout for first response
      resetStreamTimeout();

      // Create streamHandler before starting any API calls
      const streamHandler = new StreamHandler(selectedLocation!, (updates) => {
        if (tripData && selectedLocation) {
          updateLocation(selectedLocation.id, updates);
        }
      });

      // Create a promise that will resolve when streaming is complete
      const streamComplete = new Promise<void>((resolve, reject) => {
        // Check if already aborted
        if (signal.aborted) {
          reject(new Error("Stream aborted"));
          return;
        }

        AIService.submitChat(
          tripContext,
          chatId || "",
          message,
          async (data) => {
            resetStreamTimeout();

            if (typeof data === "string" && data.startsWith("data: ")) {
              try {
                const jsonStr = data.slice(6);
                const parsed = JSON.parse(jsonStr);

                if (parsed.type === "stream_init" && parsed.data?.stream_id) {
                  streamId = parsed.data.stream_id;

                  // Start streaming as soon as we get the streamId
                  if (streamId && !streamStarted) {
                    streamStarted = true;
                    await AIService.streamChat(
                      chatId || "",
                      streamId,
                      (jsonData) => {
                        // Reset timeout on each chunk
                        resetStreamTimeout();

                        if (signal.aborted) {
                          return;
                        }

                        if (jsonData?.data?.type === "awaiting_response") {
                          clearAll();
                          setSuggestionsLoading(true);
                        }
                        if (
                          jsonData?.data?.type === "flight_search_processing"
                        ) {
                          logger.log("FLIGHT SEARCH PROCESSING", jsonData);
                          setFlightSearchMessage(jsonData?.data?.text || "");
                        }
                        if (jsonData?.status === "in-progress") {
                          try {
                            if (jsonData?.data?.type === "text") {
                              setChatMessages(
                                selectedLocation?.id || "",
                                (prevMessages) => {
                                  const updatedMessages = [...prevMessages];
                                  const lastMessage =
                                    updatedMessages[updatedMessages.length - 1];
                                  if (lastMessage && !lastMessage.isUser) {
                                    lastMessage.text +=
                                      jsonData?.data?.text || "";
                                    lastMessage.status = "sent";
                                  }
                                  return updatedMessages;
                                }
                              );
                              scrollToBottom();
                            }

                            // Process any actionable items if needed
                            if (
                              jsonData?.data?.type === "actionable_item" ||
                              jsonData?.data?.type === "flight_search_results"
                            ) {
                              setSuggestionsLoading(false);
                              logger.log("ACTIONABLE ITEM", jsonData);
                              streamHandler.handleChunk(jsonData.data);
                            }
                          } catch (error) {
                            console.error("Error parsing stream data:", error);
                          }
                        }
                      },
                      (error) => {
                        console.error("Streaming error:", error);
                        setChatMessages(
                          selectedLocation?.id || "",
                          (prevMessages) => {
                            const updatedMessages = [...prevMessages];
                            const lastMessage =
                              updatedMessages[updatedMessages.length - 1];
                            if (lastMessage && !lastMessage.isUser) {
                              lastMessage.status = "error";
                              lastMessage.text =
                                "An error occurred. Please try again.";
                            }
                            return updatedMessages;
                          }
                        );
                        resolve(); // Resolve the promise even on error
                      }
                    )
                      .then(() => {
                        resolve(); // Resolve when streaming is complete
                      })
                      .catch((error) => {
                        console.error("Error in streamChat:", error);
                        resolve(); // Resolve even on error
                      });
                  }
                }
              } catch (error) {
                console.error("Error parsing stream data:", error);
              }
            }
          }
        );

        // Set up abort listener
        signal.addEventListener("abort", () => {
          reject(new Error("Stream aborted"));
        });
      });

      // Wait for both submitChat and streamComplete
      await streamComplete;
    } catch (error) {
      console.error("Error in chat streaming:", error);
      clearAll();

      // Only update the message if we're not clearing the chat
      if (!isClearing) {
        setChatMessages(selectedLocation?.id || "", (prevMessages) => {
          const updatedMessages = [...prevMessages];
          const lastMessage = updatedMessages[updatedMessages.length - 1];
          if (lastMessage && !lastMessage.isUser) {
            lastMessage.status = "error";
            lastMessage.text =
              "Sorry, couldn't process your request. Please try again.";
          }
          return updatedMessages;
        });
      }
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setIsLoading(false);
      setIsStreaming(false);
      setFlightSearchMessage("");
      setAwaitMessage("");
    }
  };

  const copyToClipboard = async (messageId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      const updatedMessages = [...chatMessages];
      const message = updatedMessages.find((msg) => msg.id === messageId);
      if (message) {
        message.copied = true;
      }
      setChatMessages(selectedLocation?.id || "", updatedMessages);
      setTimeout(() => {
        const updatedMessages = [...chatMessages];
        const message = updatedMessages.find((msg) => msg.id === messageId);
        if (message) {
          message.copied = false;
        }
        setChatMessages(selectedLocation?.id || "", updatedMessages);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleClearChat = async () => {
    try {
      setIsClearing(true);

      // Abort any ongoing stream
      abortStream();

      await AIService.clearChat(chatId || "");
      setChatMessages(selectedLocation?.id || "", []);
      clearAll();
      queryClient.invalidateQueries({
        queryKey: [
          "chatHistory",
          tripData?.id,
          selectedLocation?.waytora_id,
          selectedLocation?.id,
        ],
      });
      setShowRecommendations(false);
      setShowAccommodations(false);
      setInput("");
    } catch (error) {
      console.error("Error clearing chat:", error);
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-9.5rem)] bg-neutral-100 p-2 rounded-lg 2xl:h-[calc(100vh-10.5rem)]">
      <div className="flex flex-col w-full h-full bg-white rounded-lg">
        <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-200">
          <div className="flex items-center space-x-3 py-1.5">
            <h2 className="text-sm font-medium text-neutral-700 flex items-center gap-2">
              Chat with Tora
            </h2>
            <span className="text-xs font-light text-indigo-500 bg-indigo-50 italic ring-1 ring-indigo-200 px-2 py-0.5 rounded-full">
              beta
            </span>
          </div>
          {chatMessages && chatMessages.length > 0 ? (
            <button
              onClick={handleClearChat}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
              title="Clear chat history"
            >
              {isClearing ? (
                <>
                  <RefreshCw
                    className="w-3.5 h-3.5 animate-spin"
                    strokeWidth={1.5}
                  />
                  <span>Reset</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span>Reset</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleClearChat}
              disabled={true}
              className="opacity-50 flex items-center space-x-1.5 px-3 py-1.5 text-xs text-neutral-600 rounded-md transition-colors duration-200"
              title="Clear chat history"
            >
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>Reset</span>
            </button>
          )}
        </div>

        {/* {chatError && (
          <div className="p-3 bg-red-50 text-red-600 text-sm">
            Failed to load chat history. Please try refreshing.
          </div>
        )} */}

        <section
          className="relative flex-1 overflow-y-auto p-4 text-[12.5px] no-scrollbar"
          aria-live="polite"
        >
          <div className="h-full">
            {chatHistoryLoading || !user ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, index) => (
                  <MessageSkeleton key={index} isUser={index % 2 === 0} />
                ))}
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-xl font-medium text-neutral-800">
                    Start a conversation with Tora
                  </h2>
                  <p className="text-sm text-neutral-500 font-light">
                    Choose a topic or type your own question
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 w-full max-w-lg px-4">
                  {CONVERSATION_STARTERS(selectedLocation?.name || "").map(
                    (starter) => (
                      <button
                        key={starter.text}
                        onClick={() => handleSend(starter.prompt)}
                        className="flex items-center justify-between px-4 h-14 rounded-2xl border border-neutral-200 bg-white hover:bg-indigo-50/50 hover:border-indigo-300 hover:shadow transition-all duration-200 group relative overflow-hidden"
                      >
                        <div className="w-8 h-8 2xl:w-10 2xl:h-10 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors duration-200">
                          {starter.icon}
                        </div>
                        <span className="flex-1 ml-3 text-xs 2xl:text-sm font-light text-neutral-700 group-hover:text-indigo-700 transition-colors duration-200 text-left">
                          {starter.text}
                        </span>
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {chatMessages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    user={user}
                    copyToClipboard={copyToClipboard}
                    awaitMessage={awaitMessage}
                    flightSearchMessage={flightSearchMessage}
                  />
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </section>

        {processingMessage && (
          <div className="p-3 bg-indigo-50 text-indigo-600 text-sm">
            {processingMessage}
          </div>
        )}

        <ChatInput
          isLoading={isLoading}
          onSend={handleSend}
          messageSending={messageSending}
          disabled={isStreaming}
        />
      </div>
    </div>
  );
};

export default ToraContent;
